<script setup lang="ts">
import { computed } from 'vue';
import { useQuoteByType } from '~/composables/useQuoteByType';
import { VALID_APARTMENT_TYPES, type ApartmentTypeId } from '~~/server/db/apartment-types';


const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { trackEvent } = useAnalytics();

const checkin = computed(() => (typeof route.query.from === 'string' ? route.query.from : ''));
const checkout = computed(() => (typeof route.query.to === 'string' ? route.query.to : ''));
const guests = computed(() => {
  const g = Number(route.query.guests);
  return Number.isFinite(g) && g > 0 ? Math.trunc(g) : 0;
});

const enabled = computed(() => !!checkin.value && !!checkout.value && guests.value > 0);

const { quotes, error, refresh } = useQuoteByType({
  checkin,
  checkout,
  guests,
});

const nights = computed(() => {
  if (!checkin.value || !checkout.value) return 0;
  const a = new Date(checkin.value + 'T00:00:00Z').getTime();
  const b = new Date(checkout.value + 'T00:00:00Z').getTime();
  return Math.round((b - a) / 86_400_000);
});

const tripSummary = computed(() => {
  if (!enabled.value || nights.value === 0) return '';
  const dates = `${checkin.value} → ${checkout.value}`;
  const nightsLabel = t('prices.calculator.nights', { n: nights.value }, nights.value);
  const guestsLabel = t('prices.calculator.guestsCount', { n: guests.value }, guests.value);
  return t('prices.calculator.trip', { dates, nights: nightsLabel, guests: guestsLabel });
});

const searchHref = computed(() =>
  localePath({
    path: '/search',
    query: { from: checkin.value, to: checkout.value, guests: guests.value },
  }),
);

function onCtaClick(type: ApartmentTypeId, totalUsd: number, nightCount: number) {
  trackEvent('prices_calculator_cta_click', { type, total_usd: totalUsd, nights: nightCount });
}

// Hide the entire <li> when result is no_listings (data defect, skip row).
function isNoListings(type: ApartmentTypeId): boolean {
  const r = quotes.value?.[type];
  return !!r && !r.available && r.reason === 'no_listings';
}
</script>

<template>
  <div
    v-if="enabled"
    class="bg-white/60 dark:bg-slate-900/40"
  >
    <p class="px-4 md:px-6 pt-1 md:pt-2 pb-3 font-body text-sm text-slate-600 dark:text-slate-300">
      {{ tripSummary }}
    </p>

    <div
      v-if="error"
      class="mx-4 md:mx-6 mb-4 md:mb-6 rounded-xl border border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-950/30 p-4 flex items-center justify-between gap-4"
    >
      <span class="font-body text-sm text-red-700 dark:text-red-300">
        {{ t('prices.calculator.error') }}
      </span>
      <button
        type="button"
        class="rounded-full border border-red-400 px-4 py-1 font-body text-xs uppercase text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40"
        :aria-label="t('prices.calculator.error')"
        @click="refresh"
      >
        ↻
      </button>
    </div>

    <ul v-else class="divide-y divide-slate-200 dark:divide-slate-700 border-t border-slate-200 dark:border-slate-700">
      <template
        v-for="type in VALID_APARTMENT_TYPES"
        :key="type"
      >
        <li v-if="!isNoListings(type)">
          <PricesQuoteRowItem
            :type="type"
            :result="quotes?.[type]"
            :search-href="searchHref"
            @cta-click="onCtaClick"
          />
        </li>
      </template>
    </ul>
  </div>
</template>
